/**
 * `FixDrawer` — right-side slide-over that opens from any readiness
 * row (fail or pass) via the "Evidence" or "Show me how" link.
 *
 * Round 4 (founder requests, 2026-10-02): every FAILING drawer now says
 * EXACTLY what is wrong (with real values we found), gives the exact
 * concrete fix (numbered, platform-aware), and includes a copy-pasteable
 * code block + Copy button wherever a merchant needs to paste text
 * somewhere. The pure snippet builders live in
 * `@sill/types/readiness-fixes` — tested in isolation, no React. Every
 * paste prefills the merchant's real domain (never example.com).
 *
 * Shape:
 *   - FAILING check: "What's wrong" (specific values) → "How to fix it"
 *     (numbered, with inline <CodeBlock> + Copy where applicable) →
 *     "Why it matters" (one specific sentence, never generic) →
 *     primary action.
 *   - PASSING check: "What we checked" / "What we found" with concrete
 *     facts (URLs, response codes, per-bot rows). No "How to fix it" —
 *     nothing is wrong.
 *
 * Discrepancy drawers render the three sides explicitly:
 *   - "Your agent card says:" the current card profile value.
 *   - "Your site says:" the scan description (rendered as INERT text,
 *     never HTML/markdown — LLM output from untrusted site content).
 *   - "Where we saw it:" the sources, mapped to human labels.
 * Fix options are three buttons: update the card (inline editor), update
 * the site (muted hint for which page), or "The card is right" which
 * uses the existing dismiss mechanism.
 */

import type { ReactNode } from 'react';

import type { InstallCoverage } from '@sill/types/api';
import {
  agentCardLinkTag,
  catalogProductJsonLd,
  cloudflareCrawlerFixSteps,
  cloudflareWafSkipRule,
  contentSignalLine,
  discrepancyFieldLabel,
  discrepancySourceLabels,
  llmsTxtStarter,
  organizationJsonLd,
  productJsonLd,
  robotsAllowBlock,
  robotsStarterFile,
  sitemapRobotsLine,
} from '@sill/types';
import type {
  CatalogProductFacts,
  CatalogProductForJsonLd,
  ReadinessCheck,
  ReadinessDiscrepancy,
  ReadinessStep,
  Policy,
} from '@sill/types';
import { useState } from 'react';

import { useCatalogSample, type CatalogSampleProduct } from './useCatalogSample.js';
import { InlineEditValue } from './InlineEditValue.js';

import { useC } from '../../lib/theme.js';
import { humanSkillName as humanSkillLabel } from '../../lib/skill-gating-reason.js';
import { Drawer } from '../../components/atoms/Drawer.js';
import { useToast } from '../../app/providers.js';
import {
  actionCopy as installActionCopy,
} from '../sites/InstallCoverageCard.js';
import { monoFont, sansFont, serifFont } from './fonts.js';

export interface FixDrawerProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly step: ReadinessStep | null;
  readonly check: ReadinessCheck | null;
  readonly domain: string;
  readonly install: InstallCoverage | null;
  readonly checked_at: string | null;
  readonly onCheckAgain: () => void;
  /** Navigate to Settings sub-tabs when a step points there. */
  readonly onOpenInstall: () => void;
  readonly onOpenCardEditor: () => void;
  readonly onOpenLlmsEditor: () => void;
  /** Deep-link to Settings → Skills with the skills list outlined. */
  readonly onOpenSkills: () => void;
  /** Deep-link to Guardrails (policy editor). */
  readonly onOpenGuardrails: () => void;
  /** The merchant's `site_key_hash` — threaded into agent-card link
   *  snippets so the drawer shows the exact URL the Install tab shows. */
  readonly site_key_hash?: string | null | undefined;
  /** The merchant's site_id — used by the structured-data branch to
   *  fetch a small catalog sample (`useCatalogSample`) and prefill
   *  Product JSON-LD with real products. Optional: when absent the
   *  drawer falls back to the placeholder template, which keeps the
   *  existing behaviour. */
  readonly site_id?: string | null | undefined;
  /** Per-bot robots.txt classification (ADR-0198 shape). */
  readonly ai_bots_status?: Record<string, 'allowed' | 'blocked' | 'not_mentioned'> | null;
  /** Robots.txt general-rule classification. */
  readonly ai_access?: 'open' | 'blocked' | 'partial' | 'unknown' | null;
  /** The MCP tool count — surfaced in the MCP passing-check evidence. */
  readonly mcp_tools_count?: number | null;
  /** Number of skills the merchant has TURNED ON (enabled=true) — the
   *  MCP "N turned on · 0 live" discrepancy. Threaded from Settings
   *  → Skills so the drawer can say "1 turned on but 0 live" and name
   *  the first gating reason. */
  readonly enabled_skill_count?: number | null;
  /** Short, merchant-readable reason the first enabled-but-not-live
   *  skill is stuck (e.g. "Browse catalog needs a product catalog or
   *  a connected store."). Null when nothing is stuck or we don't
   *  know yet. Composed by the caller using the Settings → Skills
   *  `gating_reason`. */
  readonly first_stuck_reason?: string | null;
  /** The merchant's agent card URL (link in evidence). */
  readonly agent_card_url?: string | null;
  readonly llms_txt_url?: string | null;
  readonly llms_txt_preview?: string | null;
  readonly rule_count?: number | null;
  /** Active guardrail-policy rules (names + descriptions). The
   *  `display_name` is always resolved by the caller (either the policy
   *  row's own `display_name`, the catalog `name`, or the rule type as
   *  a last resort — never a bare `r05`). `configured_value` summarises
   *  the tunable parameter when the rule has one (cap amount, max
   *  validity, …). */
  readonly active_rules?: ReadonlyArray<{
    readonly rule_id?: string;
    readonly display_name?: string | null;
    readonly description?: string | null;
    readonly configured_value?: string | null;
  }>;
  readonly card_fields_present?: readonly string[];
  readonly card_fields_missing?: readonly string[];
  /** Discrepancy flags from the latest site scan — resolved per step id. */
  readonly discrepancies?: readonly ReadinessDiscrepancy[];
  /** Current agent-card profile value for a field — threaded into
   *  discrepancy drawers so "Your agent card says:" shows a real value. */
  readonly card_profile_value?: (field_id: string) => string | null;
  /** Live skills list — used in the "skills" discrepancy drawer. */
  readonly live_skill_ids?: readonly string[];
  /** Suggested skills (scan) — used in mcp.no_tools fix. */
  readonly suggested_skills?: readonly { readonly skill_id: string }[];
  /** Suggested card-profile fields (scan) — used in card.field drawers. */
  readonly suggested_card_fields?: readonly { readonly field_id: string; readonly value: string }[];
  /** Suggested policy URLs (scan) — used in policy.* drawers. */
  readonly suggested_policy_urls?: readonly { readonly kind: string; readonly url: string }[];
  /** ADR-0222 + ADR-0224 — business-link URLs detected on the homepage.
   *  Rendered in the business_links.unpublished drawer so the merchant
   *  sees the specific links we would hand to agents. */
  readonly detected_business_links?: readonly {
    readonly kind:
      | 'pricing'
      | 'demo'
      | 'signup'
      | 'booking'
      | 'contact'
      | 'status'
      | 'trust';
    readonly url: string;
  }[];
  /** ADR-0222 + ADR-0224 — one-click publish for every detected
   *  business link. The handler calls PUT per slot (saves URL +
   *  enables), surfaces a success toast, and returns the list of slots
   *  that failed so the drawer can show which ones didn't land. */
  readonly onPublishBusinessLinks?: (
    links: readonly {
      readonly kind:
        | 'pricing'
        | 'demo'
        | 'signup'
        | 'booking'
        | 'contact'
        | 'status'
        | 'trust';
      readonly url: string;
    }[],
  ) => Promise<readonly { readonly kind: string; readonly reason: string }[]>;
  /** Business name — used to pre-fill JSON-LD / llms.txt starter. */
  readonly business_name?: string | null;
  /** The full checks list — used by the "update the card" button in a
   *  skills discrepancy drawer to resolve which checks the merchant can
   *  turn on. */
  readonly dismissDiscrepancy?: (step_id: string) => void;
  readonly policy?: Policy | null;
  /** Save handler for inline policy-URL / return-window edits in the
   *  "What we found" section of a PASSING drawer. Receives the
   *  compliance kind (`seller_tos_url` / `seller_privacy_policy_url` /
   *  `returns_policy`) + the new value. Error → thrown with a short
   *  reason that renders inline under the input. */
  readonly onInlineSavePolicy?: (
    kind: 'seller_tos_url' | 'seller_privacy_policy_url' | 'returns_policy',
    value: string,
  ) => Promise<void>;
  readonly current_tos_url?: string | null;
  readonly current_privacy_url?: string | null;
  readonly current_returns_url?: string | null;
}

interface Primary {
  readonly label: string;
  readonly onClick: () => void;
}

interface DrawerContent {
  readonly kicker: string;
  readonly title: string;
  readonly whatWeChecked?: ReadonlyArray<readonly [string, ReactNode]>;
  readonly whatsWrong?: ReadonlyArray<readonly [string, ReactNode]>;
  readonly howToFix?: readonly FixStep[];
  readonly whatWeFound?: ReadonlyArray<readonly [string, ReactNode]>;
  readonly whyItMatters?: string;
  readonly primary?: Primary | undefined;
  readonly secondary?: Primary | undefined;
  readonly tertiary?: Primary | undefined;
  readonly isPass: boolean;
  /** Discrepancy drawers render three labelled rows before the fix options. */
  readonly discrepancy?: {
    readonly cardSays: ReactNode;
    readonly siteSays: string;
    readonly sources: string;
  };
}

/** A fix step may be plain text, or text + an inline Copy-pasteable snippet. */
interface FixStep {
  readonly text: ReactNode;
  readonly code?: { readonly value: string; readonly caption?: string } | undefined;
}

/** Promote a passing-row ReadinessCheck to a pseudo step so both shapes share logic. */
function stepFromCheck(check: ReadinessCheck): ReadinessStep {
  return {
    step_id: check.step_id ?? check.check_id,
    check_id: check.check_id,
    group: check.group,
    severity: check.severity,
    emerging: check.emerging,
    headline: check.headline,
    evidence: check.evidence,
    action: check.action,
  };
}

function listBlockedBots(
  bots: Record<string, 'allowed' | 'blocked' | 'not_mentioned'> | null | undefined,
): string[] {
  if (!bots) return [];
  const out: string[] = [];
  for (const [bot, status] of Object.entries(bots)) {
    if (status === 'blocked') out.push(bot);
  }
  return out;
}

/* ------------------------------------------------------------------ */
/* Pure content resolver                                               */
/* ------------------------------------------------------------------ */

function content(
  step: ReadinessStep | null,
  check: ReadinessCheck | null,
  props: FixDrawerProps,
  catalog_sample: readonly CatalogSampleProduct[],
  catalog_pick_index: number,
  onPickProduct: (index: number) => void,
): DrawerContent | null {
  const target = step ?? (check !== null ? stepFromCheck(check) : null);
  if (target === null) return null;
  const checkedWhen = props.checked_at === null
    ? '—'
    : new Date(props.checked_at).toUTCString().replace('GMT', 'UTC');
  const domain = props.domain;
  const passing = check !== null && (check.state === 'pass' || check.state === 'na');
  const platform = props.install?.platform ?? null;
  const cdn = props.install?.cdn ?? null;
  const isCloudflare = cdn === 'cloudflare';
  const isWordPress = platform === 'wordpress';
  const isShopify = platform === 'shopify';
  const isWoo = platform === 'woocommerce';

  // -----------------------------------------------------------------
  // Install-coverage steps
  // -----------------------------------------------------------------
  if (target.step_id.startsWith('install.') && props.install !== null && props.install.action !== null) {
    const copy = installActionCopy(props.install.action, domain);
    return {
      isPass: false,
      kicker: 'Discoverable · Install coverage',
      title: target.headline,
      whatsWrong: [
        ['Site', domain],
        ['Browser install', props.install.browser.state],
        ['Server-side install', props.install.server_side.state],
        ['Discovery files', props.install.discovery.agent_card],
      ],
      howToFix: [{ text: copy.body }, { text: `${copy.cta} — opens the Install tab in Settings.` }],
      whyItMatters: 'Without an install, every protocol surface Sill offers is dark to AI clients.',
      primary: { label: 'Open Install settings', onClick: props.onOpenInstall },
    };
  }

  // -----------------------------------------------------------------
  // Crawler access
  // -----------------------------------------------------------------
  if (target.check_id === 'crawler.access' || target.step_id === 'crawler.blocked') {
    if (passing) {
      return {
        isPass: true,
        kicker: 'Discoverable · Crawler access',
        title: 'Crawlers can reach your pages',
        whatWeChecked: [
          ['Request', `GET https://${domain}/`],
          ['Answer', '200 OK'],
          ['Checked', checkedWhen],
        ],
        whatWeFound: [
          ['Reachable from a generic crawler', <Link key="link" href={`https://${domain}/`}>https://{domain}/</Link>],
          ['WAF / bot shield', 'none observed'],
        ],
      };
    }
    const cfSteps = cloudflareCrawlerFixSteps(listBlockedBots(props.ai_bots_status));
    const wafRule = cloudflareWafSkipRule();
    const steps: FixStep[] = isCloudflare ? [
      { text: 'Open the Cloudflare dashboard for this site.' },
      { text: 'Go to Security → Bots and open AI Crawl Control. For each assistant you want to allow (ChatGPT, Claude, Perplexity, Google-Extended, Googlebot, Bingbot), set the action to Allow.' },
      {
        text: 'In Security → WAF → Custom rules, add a rule with Action = Skip and this expression:',
        code: { value: wafRule, caption: 'Cloudflare WAF expression' },
      },
      { text: 'Save, wait a minute, then come back and press Check again.' },
    ] : [
      { text: 'In your host or firewall, allow verified AI crawlers (ChatGPT, Claude, Perplexity, Google-Extended, Googlebot, Bingbot) from the server IPs the vendors publish.' },
      { text: 'If you run a WAF (Fastly, Imperva, AWS WAF, Akamai), add a bypass rule for verified bot categories "AI Crawler", "AI Assistant" and "Search Engine Crawler".' },
      { text: 'If you use Cloudflare in front of your origin, the "cf.verified_bot_category" expression works directly.' },
      { text: 'Save, wait a minute, then come back and press Check again.' },
    ];
    return {
      isPass: false,
      kicker: 'Discoverable · Crawler access',
      title: 'Let AI crawlers reach your site',
      whatsWrong: [
        ['Request', `GET https://${domain}/`],
        ['Answer', '403 Forbidden'],
        ['CDN', cdn ?? 'unknown'],
        ['Platform', platform ?? 'unknown'],
        ['Checked', checkedWhen],
      ],
      howToFix: steps,
      whyItMatters: 'A 403 is bot protection, not downtime. Assistants answer from what they can read; a blocked page is a dead end for every AI crawler.',
      // Primary = "Check again" lives in the footer; the drawer offers
      // a Copy block directly for Cloudflare steps.
      secondary: isCloudflare
        ? { label: 'Copy Cloudflare steps', onClick: () => void copyText(cfSteps, props) }
        : undefined,
    };
  }

  // -----------------------------------------------------------------
  // robots.txt — blocked / partial / missing / open
  // -----------------------------------------------------------------
  if (
    target.check_id === 'robots.ai_access'
    || target.check_id === 'robots.missing'
    || target.step_id?.startsWith('robots.bot_blocked:') === true
    || target.step_id === 'robots.missing'
  ) {
    const robotsUrl = `https://${domain}/robots.txt`;
    const bots = props.ai_bots_status ?? null;
    const ai_access = props.ai_access ?? null;
    const perBotRows: Array<readonly [string, ReactNode]> = [];
    if (bots !== null) {
      for (const [bot, status] of Object.entries(bots)) {
        let effective: string;
        if (status === 'allowed') effective = 'Allowed';
        else if (status === 'blocked') effective = 'Blocked';
        else if (ai_access === 'open') effective = 'Allowed (general rule)';
        else if (ai_access === 'blocked') effective = 'Blocked (general rule)';
        else effective = 'Not named';
        perBotRows.push([bot, effective]);
      }
    }
    const allNotMentioned = bots !== null && Object.values(bots).every((s) => s === 'not_mentioned');
    const generalRuleLine = allNotMentioned
      ? "Your robots.txt doesn't name these bots, so your general rules apply."
      : null;

    if (passing) {
      return {
        isPass: true,
        kicker: 'Discoverable · robots.txt',
        title: 'robots.txt lets AI bots in',
        whatWeChecked: [
          ['File', <Link key="url" href={robotsUrl}>{robotsUrl}</Link>],
          ['General rule', ai_access === 'open' ? 'Allow' : ai_access === 'blocked' ? 'Disallow' : ai_access === 'partial' ? 'Mixed' : 'Not read'],
          ['Checked', checkedWhen],
        ],
        whatWeFound: perBotRows.length === 0
          ? [['Known AI + search bots', 'not disallowed']]
          : (generalRuleLine !== null
              ? [['_lede', <span key="lede">{generalRuleLine}</span>], ...perBotRows]
              : perBotRows),
      };
    }

    // Missing file
    if (target.check_id === 'robots.missing' || target.step_id === 'robots.missing') {
      const starter = robotsStarterFile(domain);
      const platformNote: string = isWordPress
        ? 'WordPress serves a virtual robots.txt. Install a plugin like Yoast or Rank Math to edit it, or add a physical robots.txt file at the WordPress root.'
        : isShopify
          ? 'Shopify ships a robots.txt.liquid template. In the admin, open Online Store → Themes → Edit code → templates/robots.txt.liquid and paste the content below.'
          : isWoo
            ? 'WooCommerce runs on WordPress, so the robots.txt edit is the same: install Yoast or Rank Math, or add a physical file at the WordPress root.'
            : 'Upload a plain text file named robots.txt at the root of your domain. The exact spot depends on your host.';
      return {
        isPass: false,
        kicker: 'Discoverable · robots.txt',
        title: 'Add a robots.txt file',
        whatsWrong: [
          ['Looked for', <Link key="url" href={robotsUrl}>{robotsUrl}</Link>],
          ['Got', '404 Not Found'],
          ['Platform', platform ?? 'unknown'],
          ['Checked', checkedWhen],
        ],
        howToFix: [
          { text: platformNote },
          {
            text: 'Paste this starter file. It allows every crawler, declares a conservative content-signal preference, and points at your sitemap:',
            code: { value: starter, caption: 'robots.txt' },
          },
          { text: 'Save and press Check again.' },
        ],
        whyItMatters: 'robots.txt is the first file every bot checks before fetching anything else. Without one, each bot picks its own default — and the defaults vary.',
      };
    }

    // Partial / fully blocked
    const blocked = listBlockedBots(bots);
    const allowBlock = robotsAllowBlock(blocked);
    return {
      isPass: false,
      kicker: 'Discoverable · robots.txt',
      title: blocked.length === 0 ? 'Open your robots.txt to AI bots' : `Unblock ${blocked.length} ${blocked.length === 1 ? 'bot' : 'bots'} in robots.txt`,
      whatsWrong: [
        ['File', <Link key="url" href={robotsUrl}>{robotsUrl}</Link>],
        ['Blocked bots', blocked.length === 0 ? 'All AI bots (general Disallow rule)' : blocked.join(', ')],
        ['Checked', checkedWhen],
      ],
      howToFix: [
        {
          text: `Remove the Disallow lines that target ${blocked.length === 0 ? 'User-agent: * or the AI bots' : blocked.join(', ')}. Keep per-path restrictions only where you need them.`,
        },
        {
          text: 'Add these allow groups above any remaining Disallow lines. One group per bot:',
          code: { value: allowBlock, caption: 'robots.txt — paste under existing content' },
        },
        { text: `Save ${robotsUrl} and press Check again.` },
      ],
      whyItMatters: blocked.length === 0
        ? 'Your general Disallow rule applies to every AI bot — none of them will fetch any URL on this domain.'
        : `${blocked.join(', ')} will not fetch any URL on this domain while they are in a Disallow group.`,
    };
  }

  // -----------------------------------------------------------------
  // Sitemap
  // -----------------------------------------------------------------
  if (target.check_id === 'sitemap' || target.step_id === 'sitemap.missing') {
    const sitemapUrl = `https://${domain}/sitemap.xml`;
    if (passing) {
      return {
        isPass: true,
        kicker: 'Discoverable · Sitemap',
        title: 'Sitemap found',
        whatWeChecked: [
          ['Looked for', <Link key="url" href={sitemapUrl}>{sitemapUrl}</Link>],
          ['Checked', checkedWhen],
        ],
        whatWeFound: [['Served', sitemapUrl]],
      };
    }
    const line = sitemapRobotsLine(domain);
    const platformHow: string = isWordPress
      ? 'WordPress core serves /wp-sitemap.xml automatically. If you use an SEO plugin (Yoast, Rank Math, All in One SEO) it will take over and serve /sitemap_index.xml — either is fine. Make sure the plugin is active and the sitemap setting is on.'
      : isShopify
        ? 'Shopify generates /sitemap.xml automatically for every store. If the readiness check is reporting it missing, your theme or an app may be returning a 404 — check Online Store → Preferences → "Hide your storefront" is OFF.'
        : isWoo
          ? 'WooCommerce runs on WordPress — the same options apply. WordPress core serves /wp-sitemap.xml; an SEO plugin like Yoast replaces it with its own.'
          : 'Generate a sitemap.xml listing every public page and serve it at /sitemap.xml. Static-site generators (Hugo, Astro, Next.js) all have a built-in sitemap plugin.';
    return {
      isPass: false,
      kicker: 'Discoverable · Sitemap',
      title: 'Add a sitemap',
      whatsWrong: [
        ['Looked for', <Link key="url" href={sitemapUrl}>{sitemapUrl}</Link>],
        ['Got', '404 Not Found'],
        ['Platform', platform ?? 'unknown'],
        ['Checked', checkedWhen],
      ],
      howToFix: [
        { text: platformHow },
        {
          text: 'Add this line to robots.txt so crawlers find it without guessing:',
          code: { value: line, caption: 'robots.txt' },
        },
        { text: 'Save and press Check again.' },
      ],
      whyItMatters: 'A sitemap lets crawlers discover every public page without guessing URL shapes. Without one, assistants rely on in-page links and often miss category and policy pages.',
    };
  }

  // -----------------------------------------------------------------
  // Structured data
  // -----------------------------------------------------------------
  if (target.check_id === 'structured_data' || target.step_id === 'structured_data.missing') {
    if (passing) {
      return {
        isPass: true,
        kicker: 'Discoverable · Structured data',
        title: 'Product data found on your pages',
        whatWeChecked: [
          ['Pages checked', 'Homepage and up to five linked pages'],
          ['Looked for', 'JSON-LD Product / Offer / Organization'],
          ['Checked', checkedWhen],
        ],
        whatWeFound: [['JSON-LD types', 'Product, Offer, Organization']],
      };
    }
    const org = organizationJsonLd(domain, props.business_name ?? null);
    // Merchant-confirmed shipping / return / currency from the agent-
    // card profile. The resolver below reads `card_profile_value`;
    // when a field isn't set the pure builder falls back to the
    // placeholder defaults. Spread-only (exactOptionalPropertyTypes).
    const facts: CatalogProductFacts = {};
    if (props.card_profile_value !== undefined) {
      const rwd = props.card_profile_value('return_window_days');
      const shz = props.card_profile_value('shipping_zones');
      const cur = props.card_profile_value('currency');
      if (rwd !== null && rwd !== undefined) (facts as { return_window_days?: string }).return_window_days = rwd;
      if (shz !== null && shz !== undefined) (facts as { shipping_zones?: string }).shipping_zones = shz;
      if (cur !== null && cur !== undefined) (facts as { currency?: string }).currency = cur;
    }
    // Prefer a real catalog product (up to 3 to choose from) over the
    // placeholder template whenever the site has synced catalog data.
    const sample_slice = catalog_sample.slice(0, 3);
    const has_sample = sample_slice.length > 0;
    const safe_index = catalog_pick_index < sample_slice.length ? catalog_pick_index : 0;
    let prod_snippet: string;
    let paste_caption: string;
    if (has_sample) {
      const picked = sample_slice[safe_index]!;
      const forBuilder: CatalogProductForJsonLd = {
        catalog_product_id: picked.catalog_product_id,
        title: picked.title,
        ...(picked.sku !== undefined ? { sku: picked.sku } : {}),
        ...(picked.description !== undefined ? { description: picked.description } : {}),
        ...(picked.url !== undefined ? { url: picked.url } : {}),
        ...(picked.image_url !== undefined ? { image_url: picked.image_url } : {}),
        ...(picked.price_minor !== undefined ? { price_minor: picked.price_minor } : {}),
        ...(picked.currency !== undefined ? { currency: picked.currency } : {}),
        ...(picked.availability !== undefined ? { availability: picked.availability } : {}),
      };
      prod_snippet = catalogProductJsonLd(forBuilder, domain, facts);
      paste_caption = `Paste in the ${picked.title} product page <head>`;
    } else {
      prod_snippet = productJsonLd(domain);
      paste_caption = 'Paste in each product page <head>';
    }
    const product_step: FixStep = has_sample
      ? {
          text: (
            <>
              <div>Pick a product and paste the generated block in its <code>&lt;head&gt;</code>:</div>
              <div
                role="radiogroup"
                aria-label="Product to generate JSON-LD for"
                style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 6 }}
                data-testid="structured-data-product-picker"
              >
                {sample_slice.map((p, idx) => {
                  const selected = idx === safe_index;
                  return (
                    <button
                      key={p.catalog_product_id}
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      onClick={() => onPickProduct(idx)}
                      style={{
                        padding: '4px 10px',
                        background: 'transparent',
                        border: `1px solid ${selected ? 'currentColor' : 'rgba(127,127,127,0.4)'}`,
                        borderRadius: 2,
                        fontSize: 12,
                        fontFamily: sansFont,
                        cursor: 'pointer',
                        color: 'inherit',
                        fontWeight: selected ? 500 : 400,
                      }}
                    >
                      {p.title}
                    </button>
                  );
                })}
              </div>
            </>
          ),
          code: { value: prod_snippet, caption: paste_caption },
        }
      : {
          text: 'For stores, add a Product block to every product page <head>:',
          code: { value: prod_snippet, caption: paste_caption },
        };
    return {
      isPass: false,
      kicker: 'Discoverable · Structured data',
      title: 'Add product data to your pages',
      whatsWrong: [
        ['Pages checked', 'Homepage and up to five linked pages'],
        ['Looked for', 'JSON-LD Product / Offer / Organization'],
        ['Found', 'none'],
        ['Catalog products ready to use', has_sample ? String(sample_slice.length) : '0'],
        ['Checked', checkedWhen],
      ],
      howToFix: [
        {
          text: 'Add an Organization block to your homepage <head>:',
          code: { value: org, caption: 'Paste in homepage <head>' },
        },
        product_step,
        { text: 'Validate the result at search.google.com/test/rich-results, then press Check again.' },
      ],
      whyItMatters: 'Without Product markup, agents fall back to scraping the page body and guess at price, stock and shipping. Structured data makes those answers unambiguous.',
    };
  }

  // -----------------------------------------------------------------
  // Install coverage (passing)
  // -----------------------------------------------------------------
  if (target.check_id === 'install.coverage' && passing) {
    const i = props.install;
    const browser = i?.browser.state ?? 'unknown';
    const server = i?.server_side.state ?? 'unknown';
    const serverSource = i?.server_side.source ?? null;
    const serverSeen = i?.server_side.seen_at ?? null;
    const browserSeen = i?.browser.seen_at ?? null;
    const disc = i?.discovery;
    return {
      isPass: true,
      kicker: 'Discoverable · Install',
      title: 'Sill is installed on your site',
      whatWeChecked: [
        ['Site', domain],
        ['Checked', checkedWhen],
      ],
      whatWeFound: [
        ['Browser script', browser === 'live' ? `Last seen ${browserSeen ?? 'recently'}` : browser],
        ['Server-side layer', server === 'live' ? `${serverSource ?? 'live'} · last seen ${serverSeen ?? 'recently'}` : server],
        ['Agent card', disc?.agent_card === 'served'
          ? <Link key="l" href={`https://${domain}/.well-known/agent-card.json`}>{`https://${domain}/.well-known/agent-card.json`}</Link>
          : (disc?.agent_card ?? 'unknown')],
        ['Proof', disc?.proof === 'served'
          ? <Link key="l" href={`https://${domain}/.well-known/sill-proof.txt`}>{`https://${domain}/.well-known/sill-proof.txt`}</Link>
          : (disc?.proof ?? 'unknown')],
        ['llms.txt', disc?.llms_txt === 'served'
          ? <Link key="l" href={`https://${domain}/llms.txt`}>{`https://${domain}/llms.txt`}</Link>
          : (disc?.llms_txt ?? 'unknown')],
      ],
      primary: { label: 'Open Install settings', onClick: props.onOpenInstall },
    };
  }

  // -----------------------------------------------------------------
  // ADR-0222 — Business skills unpublished.
  // -----------------------------------------------------------------
  if (
    target.check_id === 'business_links'
    || target.step_id === 'business_links.unpublished'
  ) {
    const links = props.detected_business_links ?? [];
    const detected_rows: ReadonlyArray<readonly [string, ReactNode]> = links.map(
      (l) => [l.kind, l.url] as const,
    );
    const primary =
      props.onPublishBusinessLinks !== undefined && links.length > 0
        ? {
            label: 'Turn these on',
            onClick: () => {
              void props.onPublishBusinessLinks!(links);
            },
          }
        : { label: 'Open skills', onClick: props.onOpenSkills };
    const secondary =
      props.onPublishBusinessLinks !== undefined && links.length > 0
        ? { label: 'Review in Skills', onClick: props.onOpenSkills }
        : undefined;
    return {
      isPass: false,
      kicker: 'Discoverable · Business skills',
      title: 'Let agents send people to your pricing, demo, sign-up and booking pages',
      whatsWrong: [
        ['Site type', 'website'],
        ['Detected links', `${links.length}`],
        ['Published', '0'],
        ['Checked', checkedWhen],
      ],
      whatWeFound: detected_rows,
      howToFix: [
        { text: 'Confirm the detected links by pressing "Turn these on". Each one is saved as a URL on the matching skill (pricing, demo, sign-up, booking) and the skill is enabled.' },
        { text: 'Agents calling the MCP server get the URL back; your buyers never leave your own domain.' },
      ],
      whyItMatters:
        'The seven canonical Sill skills describe a store. For a website, four link-returning skills — pricing, demo, sign-up, booking — give agents something to call without exposing your forms, PII, or spam surface.',
      primary,
      ...(secondary !== undefined ? { secondary } : {}),
    };
  }

  // -----------------------------------------------------------------
  // MCP
  // -----------------------------------------------------------------
  if (
    target.check_id === 'mcp'
    || target.step_id === 'mcp.no_tools'
    || target.step_id === 'mcp.missing'
    || target.step_id === 'mcp.no_tools.website'
  ) {
    const count = props.mcp_tools_count;
    const countKnown = typeof count === 'number';
    // Website: MCP endpoint is live, but store skills don't apply.
    // The row is informational (`na`) so the drawer explains WHY and
    // routes the merchant to add a custom skill.
    if (target.step_id === 'mcp.no_tools.website') {
      return {
        isPass: false,
        kicker: 'Discoverable · MCP',
        title: 'Add a custom skill so agents have something to call',
        whatsWrong: [
          ['Endpoint', 'edge.sill.so/v1/mcp/<site>'],
          ['Live skills', countKnown ? `${count}` : '—'],
          ['Site type', 'website'],
          ['Checked', checkedWhen],
        ],
        howToFix: [
          { text: 'Open Settings → Skills.' },
          { text: 'Add a custom skill that matches what you offer — for example "Book a demo", "Start a trial", or "Request a quote".' },
          { text: 'Save. The MCP server picks up the new tool within a minute.' },
        ],
        whyItMatters:
          "The seven canonical Sill skills (browse catalog, place order, order status, …) describe a store. Your site is a website, so none of them fit. Add your own skill so agents calling your MCP server actually get work done.",
        primary: { label: 'Add a custom skill', onClick: props.onOpenSkills },
      };
    }
    if (passing) {
      return {
        isPass: true,
        kicker: 'Discoverable · MCP',
        title: 'MCP server live',
        whatWeChecked: [
          ['Endpoint', 'edge.sill.so/v1/mcp/<site>'],
          ['Checked', checkedWhen],
        ],
        whatWeFound: [
          ['Tools exposed', countKnown ? `${count} ${count === 1 ? 'skill' : 'skills'}` : '—'],
        ],
      };
    }
    if (target.step_id === 'mcp.no_tools') {
      const suggested = (props.suggested_skills ?? [])
        .map((s) => s.skill_id)
        .slice(0, 5);
      const suggestedLine = suggested.length === 0
        ? 'Browse catalog is a safe default; order status and track shipment are usually safe too.'
        : `Based on your site, we suggest turning on: ${suggested.join(', ')}.`;
      const enabled = props.enabled_skill_count ?? 0;
      const stuckReason = props.first_stuck_reason ?? null;
      // Two distinct shapes for a store with 0 live skills:
      //   (a) nothing turned on: show the suggestion list.
      //   (b) 1+ turned on but 0 live: name the FIRST reason (same text
      //       Settings → Skills shows) and route to Skills to resolve
      //       the gap, NOT to turn on another skill.
      if (enabled > 0 && stuckReason !== null) {
        return {
          isPass: false,
          kicker: 'Discoverable · MCP',
          title: `${enabled} turned on, 0 live — your MCP server has no tools`,
          whatsWrong: [
            ['Endpoint', 'edge.sill.so/v1/mcp/<site>'],
            ['Turned on', `${enabled}`],
            ['Live skills', countKnown ? `${count}` : '—'],
            ['Why', stuckReason],
            ['Checked', checkedWhen],
          ],
          howToFix: [
            { text: 'Open Settings → Skills to see each turned-on skill and what it is waiting on.' },
            { text: stuckReason },
            { text: 'Fix the gap, then re-check. The MCP server updates its tool list within a minute.' },
          ],
          whyItMatters: 'MCP is a connector protocol. A skill that is turned on but not live means the endpoint advertises nothing — agents walk away empty-handed.',
          primary: { label: 'Review skills', onClick: props.onOpenSkills },
        };
      }
      return {
        isPass: false,
        kicker: 'Discoverable · MCP',
        title: 'Turn on skills so your MCP server has tools',
        whatsWrong: [
          ['Endpoint', 'edge.sill.so/v1/mcp/<site>'],
          ['Turned on', `${enabled}`],
          ['Live skills', countKnown ? `${count}` : '—'],
          ['Suggested', suggested.length === 0 ? 'browse_catalog' : suggested.join(', ')],
          ['Checked', checkedWhen],
        ],
        howToFix: [
          { text: 'Open Settings → Skills.' },
          { text: suggestedLine },
          { text: 'Save. The MCP server updates its tool list within a minute.' },
        ],
        whyItMatters: 'MCP is a connector protocol. An endpoint with no tools accepts the connection but has nothing to call — agents walk away empty-handed.',
        primary: { label: 'Turn on skills', onClick: props.onOpenSkills },
      };
    }
    return {
      isPass: false,
      kicker: 'Discoverable · MCP',
      title: 'Expose an MCP server',
      whatsWrong: [
        ['Looked for', 'Streamable-HTTP MCP endpoint'],
        ['Got', 'not reachable'],
        ['Checked', checkedWhen],
      ],
      howToFix: [
        { text: 'Enable the MCP wrapper from Settings → Install.' },
        { text: 'Sill serves /v1/mcp/<site> at the edge once at least one skill is live.' },
      ],
      whyItMatters: 'MCP is how AI clients call your skills directly, without screen scraping.',
      primary: { label: 'Open Install settings', onClick: props.onOpenInstall },
    };
  }

  // -----------------------------------------------------------------
  // API catalog (uncounted)
  // -----------------------------------------------------------------
  if (target.check_id === 'api_catalog') {
    const url = `https://${domain}/.well-known/api-catalog`;
    return {
      isPass: passing,
      kicker: 'Discoverable · API catalog',
      title: passing ? 'API catalog served' : 'API catalog not set',
      whatWeChecked: [
        ['Looked for', <Link key="url" href={url}>{url}</Link>],
        ['Checked', checkedWhen],
      ],
      whatWeFound: passing
        ? [['Status', 'served']]
        : [
          ['Status', 'not served'],
          ['Note', 'This is the signed ARD catalog Sill hosts for you. Enable it from Install (Worker or plugin) and the file appears at the URL above.'],
        ],
    };
  }

  // -----------------------------------------------------------------
  // ARD catalog (ADR-0223) — emerging and uncounted. The signed
  // catalog is always published at the Sill edge; serving it from the
  // merchant's own origin is optional. We DO NOT silently change the
  // Worker / plugin install templates to add this file — if the drawer
  // tells the merchant to install Sill, that advice stays accurate for
  // the signed catalog; the well-known path at their own origin is a
  // separate step that requires a pointer file.
  // -----------------------------------------------------------------
  if (target.check_id === 'ard' || target.step_id === 'ard.missing') {
    const url = `https://${domain}/.well-known/ai-catalog.json`;
    const edgeCatalog = props.site_key_hash !== undefined && props.site_key_hash !== null && props.site_key_hash.length > 0
      ? `https://edge.sill.so/v1/catalog/${props.site_key_hash}.json`
      : null;
    if (passing) {
      return {
        isPass: true,
        kicker: 'Discoverable · ARD catalog',
        title: 'ARD catalog served',
        whatWeChecked: [
          ['Looked for', <Link key="url" href={url}>{url}</Link>],
          ['Checked', checkedWhen],
        ],
        whatWeFound: [['Status', 'served']],
      };
    }
    return {
      isPass: false,
      kicker: 'Discoverable · ARD catalog',
      title: 'ARD catalog not set',
      whatsWrong: [
        ['Looked for', <Link key="url" href={url}>{url}</Link>],
        ['Status', 'not served'],
        ['Checked', checkedWhen],
      ],
      howToFix: [
        { text: 'Sill already publishes a signed ARD catalog for your site at the edge.' },
        ...(edgeCatalog !== null
          ? [{ text: `Your signed catalog: ${edgeCatalog}` }]
          : []),
        { text: 'To serve the file at your own origin, publish a short pointer at /.well-known/ai-catalog.json that redirects agents to the edge URL above. The Sill Worker and WordPress plugin templates do NOT serve this path today — add a redirect in your platform or copy a static JSON pointer there.' },
      ],
      whyItMatters: 'Emerging standard, not counted. Few agents read it today; this is a one-sentence investment in future discoverability.',
    };
  }

  // -----------------------------------------------------------------
  // UCP profile (ADR-0223) — store-only, counted.
  // -----------------------------------------------------------------
  if (target.check_id === 'ucp' || target.step_id === 'ucp.missing') {
    const url = `https://${domain}/.well-known/ucp`;
    if (passing) {
      return {
        isPass: true,
        kicker: 'Discoverable · UCP profile',
        title: 'UCP profile served',
        whatWeChecked: [
          ['Looked for', <Link key="url" href={url}>{url}</Link>],
          ['Checked', checkedWhen],
        ],
        whatWeFound: [['Status', 'served']],
      };
    }
    return {
      isPass: false,
      kicker: 'Discoverable · UCP profile',
      title: 'UCP profile not served',
      whatsWrong: [
        ['Looked for', <Link key="url" href={url}>{url}</Link>],
        ['Status', 'not served'],
        ['Checked', checkedWhen],
      ],
      howToFix: [
        { text: 'Sill publishes a Universal Commerce Protocol profile for your site. The Worker or the plugin serves it at /.well-known/ucp.' },
        { text: 'Open the Install tab, deploy the Worker (or install the plugin for WordPress / WooCommerce), and run Check again.' },
      ],
      whyItMatters: 'The UCP profile is how platforms like Google read your business shape so agents can transact. Shopify stores serve it natively; everywhere else, Sill\'s install serves it.',
    };
  }

  // -----------------------------------------------------------------
  // Content signals
  // -----------------------------------------------------------------
  if (target.check_id === 'content_signals' || target.step_id === 'content_signals.missing') {
    const robotsUrl = `https://${domain}/robots.txt`;
    if (passing) {
      return {
        isPass: true,
        kicker: 'Policies · Content signals',
        title: 'AI usage declared in robots.txt',
        whatWeChecked: [
          ['File', <Link key="url" href={robotsUrl}>{robotsUrl}</Link>],
          ['Checked', checkedWhen],
        ],
        whatWeFound: [['Content-signal line', 'present']],
      };
    }
    const line = contentSignalLine();
    return {
      isPass: false,
      kicker: 'Policies · Content signals',
      title: 'Declare your AI-use preferences in robots.txt',
      whatsWrong: [
        ['File', <Link key="url" href={robotsUrl}>{robotsUrl}</Link>],
        ['Content-signal line', 'not found'],
        ['Checked', checkedWhen],
      ],
      howToFix: [
        { text: 'Content-Signal uses three keys:' },
        { text: 'search=yes/no — may the content appear in search results.' },
        { text: 'ai-input=yes/no — may assistants read it to answer a user question (RAG, summarisation).' },
        { text: 'ai-train=yes/no — may it be used to train models.' },
        {
          text: 'Add this line directly under User-agent: *. Change the yes/no values to match your preference:',
          code: { value: line, caption: 'robots.txt — under User-agent: *' },
        },
        { text: 'Save and press Check again.' },
      ],
      whyItMatters: 'A declared preference removes ambiguity when model trainers decide what they will crawl. Silence is read as "no objection".',
    };
  }

  // -----------------------------------------------------------------
  // Agent card
  // -----------------------------------------------------------------
  if (target.check_id === 'agent_card' || target.step_id === 'agent_card.missing') {
    const cardUrl = props.agent_card_url ?? `https://${domain}/.well-known/agent-card.json`;
    const present = props.card_fields_present ?? [];
    const missing = props.card_fields_missing ?? [];
    const total = present.length + missing.length;
    if (passing) {
      return {
        isPass: true,
        kicker: 'Discoverable · Agent card',
        title: 'Agent card published and signed',
        whatWeChecked: [
          ['Live card', <Link key="url" href={cardUrl}>{cardUrl}</Link>],
          ['Signature', 'verified'],
          ['Checked', checkedWhen],
        ],
        whatWeFound: [
          ['Details filled', total === 0 ? 'all available' : `${present.length} of ${total}`],
          ['Filled fields', present.length === 0 ? '—' : present.join(', ')],
          ['Missing fields', missing.length === 0 ? '—' : missing.join(', ')],
        ],
        primary: { label: 'Edit card', onClick: props.onOpenCardEditor },
      };
    }
    const linkTag = agentCardLinkTag(props.site_key_hash ?? null);
    return {
      isPass: false,
      kicker: 'Discoverable · Agent card',
      title: 'Serve a signed agent card',
      whatsWrong: [
        ['Looked for', <Link key="url" href={cardUrl}>{cardUrl}</Link>],
        ['Got', 'not served or redirecting off-domain'],
        ['Checked', checkedWhen],
      ],
      howToFix: [
        { text: 'The easiest path is to install the Sill Worker or plugin — it serves the signed card at /.well-known/agent-card.json on your domain. The Install tab walks through it.' },
        {
          text: 'If you can\'t install either, paste this <link> tag in every page <head>. It points agents at the edge-hosted card for your site:',
          code: { value: linkTag, caption: 'Paste in <head>' },
        },
        { text: 'Press Check again once the card is live.' },
      ],
      whyItMatters: 'The agent card is the first file an agent reads. A missing card forces fallback scraping and the shipping, returns and skill data a buyer-agent needs is lost.',
      primary: { label: 'Open Install settings', onClick: props.onOpenInstall },
      secondary: { label: 'Edit card', onClick: props.onOpenCardEditor },
    };
  }

  // -----------------------------------------------------------------
  // llms.txt
  // -----------------------------------------------------------------
  if (target.check_id === 'llms_txt' || target.step_id === 'llms_txt.missing') {
    const llmsUrl = props.llms_txt_url ?? `https://${domain}/llms.txt`;
    if (passing) {
      return {
        isPass: true,
        kicker: 'Discoverable · llms.txt',
        title: 'llms.txt present',
        whatWeChecked: [
          ['Live file', <Link key="url" href={llmsUrl}>{llmsUrl}</Link>],
          ['Checked', checkedWhen],
        ],
        whatWeFound: [['Preview', props.llms_txt_preview ?? '—']],
        primary: { label: 'Edit llms.txt', onClick: props.onOpenLlmsEditor },
      };
    }
    const starter = llmsTxtStarter(domain, props.business_name ?? null);
    return {
      isPass: false,
      kicker: 'Discoverable · llms.txt',
      title: 'Add an llms.txt',
      whatsWrong: [
        ['Fetched', <Link key="url" href={llmsUrl}>{llmsUrl}</Link>],
        ['Got', props.llms_txt_preview === null || props.llms_txt_preview === undefined || props.llms_txt_preview === '' ? '404 or empty body' : `thin content: "${props.llms_txt_preview.slice(0, 80)}"`],
        ['Checked', checkedWhen],
      ],
      howToFix: [
        { text: 'Open the llms.txt editor in Sill and write a short description plus links to your top product and policy pages. Sill serves the file for you.' },
        {
          text: 'If you prefer to host it yourself, start with this template. The Sill Worker or plugin can serve the same file:',
          code: { value: starter, caption: 'llms.txt' },
        },
        { text: 'Save and press Check again.' },
      ],
      whyItMatters: 'llms.txt is a short, plain-English description assistants can read without any crawling. Think of it as a product summary for AI.',
      primary: { label: 'Edit llms.txt', onClick: props.onOpenLlmsEditor },
    };
  }

  // -----------------------------------------------------------------
  // Guardrails
  // -----------------------------------------------------------------
  if (target.check_id === 'guardrails.active' || target.step_id === 'guardrails.none') {
    const rc = props.rule_count ?? null;
    const rules = props.active_rules ?? [];
    if (passing) {
      // §D — Ordered list, each rule collapsible, "Expand all" link in
      // the section header, configured value right-aligned on each row.
      // All collapsed by default. The "Open Guardrails" primary stays.
      return {
        isPass: true,
        kicker: 'Policies · Guardrails',
        title: `${rc ?? rules.length} rules checked on every agent action.`,
        whatWeChecked: [
          ['Policy', 'loaded'],
          ['Checked', checkedWhen],
        ],
        whatWeFound: rules.length === 0
          ? [['Active rules', '—']]
          : [[
              '_rules_ordered_list',
              <GuardrailsRulesList
                key="rules"
                rules={rules}
                onOpenGuardrails={props.onOpenGuardrails}
              />,
            ]],
        primary: { label: 'Open Rules', onClick: props.onOpenGuardrails },
      };
    }
    return {
      isPass: false,
      kicker: 'Policies · Guardrails',
      title: 'Turn on your guardrails',
      whatsWrong: [
        ['Policy', 'loaded'],
        ['Rules active', '0'],
        ['Checked', checkedWhen],
      ],
      howToFix: [
        { text: 'Open Rules. Sill ships starter rules you can turn on in one click:' },
        { text: '— per-mandate amount cap (block above $500 unless pre-approved)' },
        { text: '— quantity cap per order (block above 10 units)' },
        { text: '— refund escalation (hold refunds above $100 for human review)' },
        { text: '— after-hours authorisation (escalate orders between 1 and 5 am local time)' },
        { text: 'Turn on the ones that match how your store operates and press Check again.' },
      ],
      whyItMatters: 'Guardrails are the rules Sill checks on every agent action. Without them, agents act without any amount, quantity or refund limits.',
      primary: { label: 'Open Rules', onClick: props.onOpenGuardrails },
    };
  }

  // -----------------------------------------------------------------
  // Signed records (always passing)
  // -----------------------------------------------------------------
  if (target.check_id === 'audit.signed_records') {
    return {
      isPass: true,
      kicker: 'Policies · Signed records',
      title: 'Every agent action is signed and chained',
      whatWeChecked: [['Checked', checkedWhen]],
      whatWeFound: [
        ['Record envelope', 'ed25519-signed, Merkle-chained'],
        ['Export', 'Any time, from the Activity page'],
      ],
    };
  }

  // -----------------------------------------------------------------
  // Policy URLs (ToS / privacy / returns)
  // -----------------------------------------------------------------
  if (target.step_id?.startsWith('policy.') === true && target.step_id?.endsWith('_missing') === true) {
    const kindLabel: string = target.step_id === 'policy.tos_missing' ? 'terms of service'
      : target.step_id === 'policy.privacy_missing' ? 'privacy policy'
      : 'returns policy';
    const sugKinds: readonly string[] =
      target.step_id === 'policy.tos_missing' ? ['seller_tos_url', 'terms', 'tos']
      : target.step_id === 'policy.privacy_missing' ? ['seller_privacy_policy_url', 'privacy', 'privacy_policy']
      : ['refund_policy', 'return_policy', 'returns'];
    const sug = (props.suggested_policy_urls ?? []).find((u) => sugKinds.includes(u.kind));
    const suggestedUrl = sug?.url ?? null;
    return {
      isPass: false,
      kicker: 'Policies · Policy URL',
      title: `Add your ${kindLabel} URL`,
      whatsWrong: [
        ['Site', domain],
        ['Agent card field', `${kindLabel} URL`],
        ['Current value', 'not set'],
        ['Suggested', suggestedUrl !== null
          ? <Link key="sug" href={suggestedUrl}>{suggestedUrl}</Link>
          : 'none found'],
        ['Checked', checkedWhen],
      ],
      howToFix: [
        suggestedUrl !== null
          ? {
            text: `We found a ${kindLabel} page at the URL above. Save it inline on this row, or open the agent card editor to type a different one.`,
          }
          : { text: `Find your public ${kindLabel} page on this site. Open the agent card editor and paste the URL.` },
        { text: 'Save. Your agent card redeploys automatically.' },
      ],
      whyItMatters: `Agents read ${kindLabel} before they place a paid order. If the URL isn't on the agent card, buyer-side policy checks fail and the agent either refuses to buy or buys without them.`,
      primary: { label: 'Open agent card editor', onClick: props.onOpenCardEditor },
    };
  }

  // Passing policy URL rows — inline edit in "What we found".
  if (
    target.check_id === 'policy.tos'
    || target.check_id === 'policy.privacy'
    || target.check_id === 'policy.returns'
  ) {
    if (passing) {
      const kind =
        target.check_id === 'policy.tos' ? 'seller_tos_url' as const
        : target.check_id === 'policy.privacy' ? 'seller_privacy_policy_url' as const
        : 'returns_policy' as const;
      const current =
        kind === 'seller_tos_url' ? (props.current_tos_url ?? null)
        : kind === 'seller_privacy_policy_url' ? (props.current_privacy_url ?? null)
        : (props.current_returns_url ?? null);
      const label =
        kind === 'seller_tos_url' ? 'Terms of service URL'
        : kind === 'seller_privacy_policy_url' ? 'Privacy policy URL'
        : 'Returns policy URL';
      return {
        isPass: true,
        kicker: 'Policies · Policy URL',
        title: target.headline,
        whatWeChecked: [['Checked', checkedWhen]],
        whatWeFound: [[
          label,
          props.onInlineSavePolicy !== undefined
            ? <InlineEditValue
                key={target.check_id}
                label=""
                value={current}
                kind="url"
                site_domain={domain}
                onSave={async (next) => { await props.onInlineSavePolicy!(kind, next); }}
                onOpenFullEditor={props.onOpenCardEditor}
                data_testid={`fixdrawer-inline-${target.check_id}`}
              />
            : (current !== null && current.startsWith('http')
                ? <Link key="l" href={current}>{current}</Link>
                : (target.evidence || 'linked')),
        ]],
      };
    }
  }

  // -----------------------------------------------------------------
  // Discrepancy — card vs site
  // -----------------------------------------------------------------
  if (target.step_id?.startsWith('scan.discrepancy:') === true) {
    const flag = resolveDiscrepancy(target, props.discrepancies ?? []);
    const label = discrepancyFieldLabel(flag?.field ?? 'value');
    const field = flag?.field ?? '';
    const sources = discrepancySourceLabels(flag?.sources ?? []);
    const field_lower = field.toLowerCase();
    const is_commerce_applicability = field_lower.includes('commerce_applicability')
      || field_lower.includes('commerce');
    const is_skills_shaped = field === 'skills' || is_commerce_applicability;

    // "Your agent card says:" — ALWAYS show something concrete.
    //   - skills / commerce_applicability: the live/enabled store
    //     skills published on the card (filtered to just the store set
    //     so the drawer names what's actually in dispute).
    //   - other fields: the card profile value threaded in from the
    //     visibility report.
    const live_store_skills = (props.live_skill_ids ?? []).filter(
      (id) =>
        id === 'browse_catalog'
        || id === 'check_availability'
        || id === 'place_order'
        || id === 'order_status'
        || id === 'track_shipment'
        || id === 'request_refund'
        || id === 'recommend',
    );
    const cardSays: ReactNode = is_skills_shaped
      ? (live_store_skills.length === 0
          ? ((props.live_skill_ids ?? []).length === 0
              ? '—'
              : (props.live_skill_ids ?? []).join(', '))
          : live_store_skills.map(humanSkillLabel).join(', '))
      : (props.card_profile_value?.(field) ?? '—');

    const siteSays = flag?.description ?? '—';
    const whichPage = field === 'returns_policy' || field === 'return_window_days'
      ? 'your returns page'
      : field === 'seller_privacy_policy_url'
        ? 'your privacy page'
        : field === 'seller_tos_url'
          ? 'your terms page'
          : field === 'shipping_zones'
            ? 'your shipping page'
            : is_skills_shaped
              ? 'the page on your site that describes what you offer (usually the homepage)'
              : 'the matching page on your website';

    // Specific wording for the "card advertises store skills on a
    // website" flag (field like `skills/commerce_applicability`). This
    // is the vbout.com shape: the site is a SaaS/website, but the
    // agent card still carries store skills from the defaults.
    if (is_commerce_applicability) {
      const list = live_store_skills.length === 0
        ? 'the store skills listed above'
        : `the store skills listed above (${live_store_skills.map(humanSkillLabel).join(', ')})`;
      return {
        isPass: false,
        kicker: 'Policies · Mismatch',
        title: 'Your agent card offers store skills, but your site is a website',
        whatsWrong: [
          ['Field', 'Store skills'],
          ['Checked', checkedWhen],
        ],
        discrepancy: { cardSays, siteSays, sources },
        howToFix: [
          { text: `Turn off ${list} in Settings → Skills.` },
          { text: 'Custom skills you added are not affected; keep the ones that match what you offer.' },
          { text: 'Save. The agent card lists live skills, so it updates within a minute.' },
        ],
        whyItMatters: 'An agent card that advertises store skills on a non-store site tells assistants your site accepts orders when it does not. That is the mismatch; the risk is a wasted call or a confused buyer.',
        primary: { label: 'Review store skills', onClick: props.onOpenSkills },
        secondary: {
          label: 'The card is right',
          onClick: () => {
            props.dismissDiscrepancy?.(target.step_id);
            props.onClose();
          },
        },
      };
    }

    return {
      isPass: false,
      kicker: 'Policies · Mismatch',
      title: `Your agent card and site disagree on ${label.toLowerCase()}`,
      whatsWrong: [
        ['Field', label],
        ['Checked', checkedWhen],
      ],
      discrepancy: { cardSays, siteSays, sources },
      howToFix: [
        { text: 'Compare the two lines above and decide which one is true today.' },
        { text: is_skills_shaped
          ? 'If your site is right, turn on the matching skills in Settings → Skills. The agent card lists live skills, so it updates within a minute.'
          : `If your site is right, update ${label.toLowerCase()} on your agent card with the editor below.` },
        { text: `If the card is right, change the wording on ${whichPage}, then choose "The card is right". The next scan checks again.` },
      ],
      whyItMatters: 'When the card and site disagree, agents may trust either source — the discrepancy is the risk, not either value on its own.',
      primary: is_skills_shaped
        ? { label: 'Review skills', onClick: props.onOpenSkills }
        : { label: 'Update card', onClick: props.onOpenCardEditor },
      secondary: {
        label: 'The card is right',
        onClick: () => {
          props.dismissDiscrepancy?.(target.step_id);
          props.onClose();
        },
      },
    };
  }

  // -----------------------------------------------------------------
  // Card field (missing + suggested from scan)
  // -----------------------------------------------------------------
  if (target.step_id?.startsWith('card.field:') === true) {
    const field = target.step_id.slice('card.field:'.length);
    const label = discrepancyFieldLabel(field);
    const sug = (props.suggested_card_fields ?? []).find((s) => s.field_id === field);
    const siteValue = sug?.value ?? '';
    return {
      isPass: false,
      kicker: 'Policies · Agent card field',
      title: `Fill in ${label.toLowerCase()} on your agent card`,
      whatsWrong: [
        ['Agent card field', label],
        ['Current card value', 'not set'],
        ['Read from your site', siteValue.length === 0 ? '—' : siteValue],
        ['Checked', checkedWhen],
      ],
      howToFix: [
        siteValue.length > 0
          ? { text: `Use the value we read from your site, or open the editor to type a different one.` }
          : { text: `Open the agent card editor and type the right value for ${label.toLowerCase()}.` },
        { text: 'Nothing is auto-applied. A merchant click is the write authority.' },
      ],
      whyItMatters: `Agents read the agent card first. A single missing field for ${label.toLowerCase()} can be the difference between an answered pre-purchase question and a dead end.`,
      primary: { label: 'Open agent card editor', onClick: props.onOpenCardEditor },
    };
  }

  // Generic fallback — still structured.
  return {
    isPass: passing,
    kicker: 'Readiness · Check',
    title: target.headline,
    whatWeChecked: [['Site', domain], ['Checked', checkedWhen]],
    ...(passing
      ? { whatWeFound: [['Result', target.evidence || '—']] }
      : {
          whatsWrong: [['Observed', target.evidence || '—']],
          howToFix: target.evidence !== '' ? [{ text: target.evidence }] : [{ text: 'Open the relevant settings tab and update the value.' }],
          whyItMatters: 'Each check surfaces one thing agents look for.',
        }),
  };
}

function resolveDiscrepancy(
  step: ReadinessStep,
  flags: readonly ReadinessDiscrepancy[],
): ReadinessDiscrepancy | null {
  // The step id is `scan.discrepancy:<hash>` where `<hash>` is
  // `hashReadinessId([field, ...sources].join('|'))`. We don't need to
  // re-hash; instead, we find the flag whose check_id (built from the
  // same shape) matches the headline we already carry.
  // Fallback: pick the first flag whose field label appears in headline.
  const h = step.headline.toLowerCase();
  for (const f of flags) {
    const label = discrepancyFieldLabel(f.field).toLowerCase();
    if (h.includes(label)) return f;
  }
  return flags[0] ?? null;
}

function copyText(text: string, props: FixDrawerProps): void {
  try {
    void navigator.clipboard.writeText(text);
  } catch {
    /* best-effort */
  }
  // Toast is handled inline by the inline <Copy> button in the drawer;
  // this out-of-band helper is only used for the Cloudflare secondary.
  void props;
}

/* ------------------------------------------------------------------ */
/* View                                                                */
/* ------------------------------------------------------------------ */

export function FixDrawer(props: FixDrawerProps): JSX.Element | null {
  const { open, onClose, step, check, onCheckAgain } = props;
  const C = useC();
  // Fetch a small sample of catalog products only when the drawer is
  // open on the structured-data step. Everywhere else the hook is
  // idle (enabled=false). When `site_id` isn't threaded through from
  // Readiness.tsx, the hook short-circuits and the drawer falls back
  // to the placeholder template — same visual as before.
  const target = step ?? (check !== null ? stepFromCheck(check) : null);
  const is_structured_data =
    target !== null &&
    (target.check_id === 'structured_data' || target.step_id === 'structured_data.missing');
  const site_id = props.site_id ?? null;
  const catalog = useCatalogSample(site_id, open && is_structured_data);
  const [pick, setPick] = useState(0);
  const data = content(step, check, props, catalog.products, pick, setPick);
  if (!open) return null;
  return (
    <Drawer open={open} onClose={onClose} title={data?.kicker ?? 'Readiness'} data-testid="readiness-fix-drawer">
      <div className="flex flex-col h-full" style={{ fontFamily: sansFont, color: C.body, background: C.surface }}>
        <div style={{ padding: '28px 24px 20px', borderBottom: `1px solid ${C.borderSoft}` }}>
          <h3 style={{ margin: 0, fontFamily: serifFont, fontWeight: 400, fontSize: 'clamp(22px, 3.4vw, 28px)', color: C.text }}>
            {data?.title ?? 'Readiness'}
          </h3>
        </div>
        <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: 26, flex: 1, overflowY: 'auto' }}>
          {data === null ? (
            <div style={{ fontSize: 13, color: C.muted }}>Nothing to fix for this check.</div>
          ) : data.isPass ? (
            <>
              {data.whatWeChecked !== undefined && data.whatWeChecked.length > 0 ? (
                <Section title="What we checked">
                  <DL rows={data.whatWeChecked} />
                </Section>
              ) : null}
              {data.whatWeFound !== undefined && data.whatWeFound.length > 0 ? (
                <Section title="What we found">
                  <DL rows={data.whatWeFound} />
                </Section>
              ) : null}
            </>
          ) : (
            <>
              {data.whatsWrong !== undefined && data.whatsWrong.length > 0 ? (
                <Section title="What's wrong">
                  <DL rows={data.whatsWrong} />
                </Section>
              ) : null}
              {data.discrepancy !== undefined ? (
                <Section title="Where it disagrees">
                  <Discrepancy
                    cardSays={data.discrepancy.cardSays}
                    siteSays={data.discrepancy.siteSays}
                    sources={data.discrepancy.sources}
                  />
                </Section>
              ) : null}
              {data.howToFix !== undefined && data.howToFix.length > 0 ? (
                <Section title="How to fix it">
                  <FixList steps={data.howToFix} />
                </Section>
              ) : null}
              {data.whyItMatters !== undefined && data.whyItMatters !== '' ? (
                <Section title="Why it matters">
                  <p style={{ margin: 0, fontSize: 14, lineHeight: 1.6, color: C.body }}>{data.whyItMatters}</p>
                </Section>
              ) : null}
            </>
          )}
        </div>
        <div style={{ padding: '18px 24px', borderTop: `1px solid ${C.borderSoft}`, display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {data?.primary !== undefined ? (
              <SecondaryButton label={data.primary.label} onClick={data.primary.onClick} />
            ) : null}
            {data?.secondary !== undefined ? (
              <SecondaryButton label={data.secondary.label} onClick={data.secondary.onClick} />
            ) : null}
            {data?.tertiary !== undefined ? (
              <SecondaryButton label={data.tertiary.label} onClick={data.tertiary.onClick} />
            ) : null}
          </div>
          {data?.isPass === false ? (
            <button
              type="button"
              onClick={() => { onCheckAgain(); onClose(); }}
              style={{
                fontFamily: sansFont, fontSize: 14, fontWeight: 600,
                padding: '12px 18px', minHeight: 44,
                background: C.lime, color: C.ink, border: `1px solid ${C.lime}`, borderRadius: 3, cursor: 'pointer',
              }}
            >
              Check again
            </button>
          ) : (
            <button
              type="button"
              onClick={onClose}
              style={{
                fontFamily: sansFont, fontSize: 14, padding: '10px 16px', minHeight: 44,
                background: 'transparent', color: C.text, border: `1px solid ${C.border}`, borderRadius: 3, cursor: 'pointer',
              }}
            >
              Close
            </button>
          )}
        </div>
      </div>
    </Drawer>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }): JSX.Element {
  const C = useC();
  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ fontSize: 14, fontWeight: 500, color: C.text }}>{title}</div>
      {children}
    </section>
  );
}

function DL({ rows }: { rows: ReadonlyArray<readonly [string, ReactNode]> }): JSX.Element {
  const C = useC();
  return (
    <dl style={{ display: 'grid', gridTemplateColumns: '150px 1fr', gap: '8px 16px', margin: 0, fontSize: 13 }}>
      {rows.map(([k, v], i) => {
        if (k === '_lede') {
          return (
            <div
              key={`_lede-${i}`}
              style={{
                gridColumn: '1 / span 2',
                color: C.body,
                fontFamily: sansFont,
                fontSize: 13,
                lineHeight: 1.5,
                marginBottom: 2,
              }}
            >
              {v}
            </div>
          );
        }
        if (k === '_rules_ordered_list') {
          return (
            <div
              key={`_rules-${i}`}
              style={{ gridColumn: '1 / span 2' }}
            >
              {v}
            </div>
          );
        }
        return <SectionRow key={`${k}-${i}`} k={k} v={v} />;
      })}
    </dl>
  );
}

/**
 * `GuardrailsRulesList` — §D ordered list: collapsed rows show the
 * number, name, and configured value right-aligned (mono); expanded
 * rows add the description, on-fail label and "Edit in Rules →"
 * link. "Expand all" lives in the header.
 */
function GuardrailsRulesList({
  rules,
  onOpenGuardrails,
}: {
  readonly rules: ReadonlyArray<{
    readonly rule_id?: string;
    readonly display_name?: string | null;
    readonly description?: string | null;
    readonly configured_value?: string | null;
  }>;
  readonly onOpenGuardrails: () => void;
}): JSX.Element {
  const C = useC();
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());
  const allOpen = rules.length > 0 && expanded.size === rules.length;
  const toggle = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    if (allOpen) setExpanded(new Set());
    else setExpanded(new Set(rules.map((r, i) => r.rule_id ?? String(i))));
  };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button
          type="button"
          onClick={toggleAll}
          style={{
            background: 'transparent',
            border: 'none',
            padding: 0,
            color: C.text,
            fontFamily: monoFont,
            fontSize: 11,
            textDecoration: 'underline',
            textDecorationColor: C.dim,
            textUnderlineOffset: '3px',
            cursor: 'pointer',
          }}
          data-testid="guardrails-expand-all"
        >
          {allOpen ? 'Collapse all' : 'Expand all'}
        </button>
      </div>
      <ol style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
        {rules.map((r, i) => {
          const id = r.rule_id ?? String(i);
          const open = expanded.has(id);
          const name = r.display_name ?? r.rule_id ?? `Rule ${i + 1}`;
          const cfg = r.configured_value ?? null;
          return (
            <li
              key={id}
              style={{
                border: `1px solid ${C.border}`,
                borderRadius: 2,
                background: C.surface,
              }}
            >
              <button
                type="button"
                onClick={() => toggle(id)}
                aria-expanded={open}
                style={{
                  width: '100%',
                  background: 'transparent',
                  border: 'none',
                  padding: '10px 12px',
                  textAlign: 'left',
                  cursor: 'pointer',
                  display: 'grid',
                  gridTemplateColumns: 'auto 1fr auto auto',
                  gap: 10,
                  alignItems: 'center',
                  color: C.text,
                }}
              >
                <span style={{ fontFamily: monoFont, fontSize: 12, color: C.muted }}>{i + 1}.</span>
                <span style={{ fontFamily: sansFont, fontSize: 13.5, color: C.text }}>{name}</span>
                <span style={{ fontFamily: monoFont, fontSize: 12, color: C.body, textAlign: 'right' }}>
                  {cfg !== null && cfg.length > 0 ? cfg : ''}
                </span>
                <span style={{ fontFamily: monoFont, fontSize: 11, color: C.muted }}>{open ? '▾' : '▸'}</span>
              </button>
              {open ? (
                <div style={{ padding: '0 12px 12px 42px', borderTop: `1px solid ${C.borderSoft}` }}>
                  <div style={{ paddingTop: 8, fontSize: 12.5, color: C.body, lineHeight: 1.5 }}>
                    {r.description ?? ''}
                  </div>
                  <button
                    type="button"
                    onClick={onOpenGuardrails}
                    style={{
                      marginTop: 8,
                      background: 'transparent',
                      border: 'none',
                      padding: 0,
                      color: C.text,
                      fontFamily: monoFont,
                      fontSize: 11,
                      textDecoration: 'underline',
                      textDecorationColor: C.dim,
                      textUnderlineOffset: '3px',
                      cursor: 'pointer',
                    }}
                  >
                    Edit in Rules →
                  </button>
                </div>
              ) : null}
            </li>
          );
        })}
      </ol>
    </div>
  );
}

function SectionRow({ k, v }: { k: string; v: ReactNode }): JSX.Element {
  const C = useC();
  return (
    <>
      <dt style={{ color: C.muted }}>{k}</dt>
      <dd style={{ margin: 0, color: C.body, fontFamily: monoFont, fontSize: 12, wordBreak: 'break-word' }}>{v}</dd>
    </>
  );
}

function FixList({ steps }: { steps: readonly FixStep[] }): JSX.Element {
  const C = useC();
  return (
    <ol
      style={{
        margin: 0,
        paddingLeft: 20,
        listStyle: 'decimal',
        fontSize: 14,
        lineHeight: 1.75,
        color: C.body,
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
      }}
    >
      {steps.map((s, i) => (
        <li key={i}>
          <div>{s.text}</div>
          {s.code !== undefined ? (
            <div style={{ marginTop: 8 }}>
              <CodeBlock value={s.code.value} caption={s.code.caption} />
            </div>
          ) : null}
        </li>
      ))}
    </ol>
  );
}

function Discrepancy({
  cardSays,
  siteSays,
  sources,
}: {
  readonly cardSays: ReactNode;
  readonly siteSays: string;
  readonly sources: string;
}): JSX.Element {
  const C = useC();
  // `siteSays` is scan description text, which carries LLM-generated
  // content from untrusted merchant pages. Render as INERT text — React's
  // text-node auto-escape plus a plain <p> keeps us safe from HTML
  // injection and markdown doesn't render.
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'grid', gridTemplateColumns: '150px 1fr', gap: '6px 16px', fontSize: 13 }}>
        <div style={{ color: C.muted }}>Your agent card says:</div>
        <div style={{ color: C.body, fontFamily: monoFont, fontSize: 12 }}>{cardSays}</div>
        <div style={{ color: C.muted }}>Your site says:</div>
        <div
          style={{ color: C.body, fontFamily: sansFont, fontSize: 13, lineHeight: 1.55 }}
          data-testid="discrepancy-site-says"
        >
          {/* Inert plain text — React escapes it. */}
          <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{siteSays}</p>
        </div>
        <div style={{ color: C.muted }}>Where we saw it:</div>
        <div style={{ color: C.body, fontFamily: monoFont, fontSize: 12 }}>{sources}</div>
      </div>
    </div>
  );
}

function CodeBlock({ value, caption }: { readonly value: string; readonly caption?: string | undefined }): JSX.Element {
  const C = useC();
  const toast = useToast();
  const onCopy = (): void => {
    try {
      void navigator.clipboard.writeText(value);
      toast({ message: 'Copied.', tone: 'mint' });
    } catch {
      toast({ message: 'Could not copy.', tone: 'amber' });
    }
  };
  return (
    <div
      style={{
        border: `1px solid ${C.border}`,
        borderRadius: 3,
        background: C.bg,
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '6px 10px',
          borderBottom: `1px solid ${C.borderSoft}`,
          fontFamily: monoFont,
          fontSize: 11,
          color: C.muted,
          letterSpacing: '0.03em',
          textTransform: 'uppercase',
        }}
      >
        <span>{caption ?? 'Copy'}</span>
        <button
          type="button"
          onClick={onCopy}
          aria-label="Copy snippet"
          style={{
            fontFamily: sansFont,
            fontSize: 12,
            padding: '4px 10px',
            minHeight: 28,
            background: 'transparent',
            color: C.text,
            border: `1px solid ${C.border}`,
            borderRadius: 3,
            cursor: 'pointer',
          }}
        >
          Copy
        </button>
      </div>
      <pre
        style={{
          margin: 0,
          padding: '10px 12px',
          fontFamily: monoFont,
          fontSize: 12,
          lineHeight: 1.55,
          color: C.body,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          maxHeight: 320,
          overflowY: 'auto',
        }}
      >
        {value}
      </pre>
    </div>
  );
}

function SecondaryButton({ label, onClick }: { label: string; onClick: () => void }): JSX.Element {
  const C = useC();
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        fontFamily: sansFont, fontSize: 14, padding: '10px 16px', minHeight: 44,
        background: 'transparent', color: C.text, border: `1px solid ${C.border}`, borderRadius: 3, cursor: 'pointer',
      }}
    >
      {label}
    </button>
  );
}

function Link({ href, children }: { href: string; children: ReactNode }): JSX.Element {
  const C = useC();
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      style={{ color: C.text, textDecoration: 'underline', textDecorationColor: C.dim, textUnderlineOffset: '3px' }}
    >
      {children}
    </a>
  );
}
